import { FileText, Shield, X } from 'lucide-react';
import { useState } from 'react';
import { useVerifyKycDocument } from 'features/customer/hooks/useVerifyKycDocument';
import { formatDateTime } from 'utils/dateFormatter';
import { getStatusColor } from '../utils';

const VERIFY_DELAY_MS = 5000;

const KycTab = ({ customer, customerId }) => {
  const [viewedDocuments, setViewedDocuments] = useState({});
  const [verifiableDocuments, setVerifiableDocuments] = useState({});
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedDocForReject, setSelectedDocForReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const { mutate: verifyDocument, isPending: isVerifyingDoc } = useVerifyKycDocument();

  const handleViewDocument = (docId, documentUrl) => {
    window.open(documentUrl, '_blank', 'noopener,noreferrer');

    if (!viewedDocuments[docId]) {
      setViewedDocuments((prev) => ({ ...prev, [docId]: Date.now() }));

      setTimeout(() => {
        setVerifiableDocuments((prev) => ({ ...prev, [docId]: true }));
      }, VERIFY_DELAY_MS);
    }
  };

  const canVerifyDocument = (docId) => {
    return verifiableDocuments[docId] === true;
  };

  const handleApproveDocument = (docId) => {
    if (canVerifyDocument(docId)) {
      verifyDocument({
        customerId,
        documentId: docId,
        verificationStatus: 'verified',
      });
    }
  };

  const handleOpenRejectModal = (doc) => {
    setSelectedDocForReject(doc);
    setRejectionReason('');
    setRejectModalOpen(true);
  };

  const handleRejectDocument = () => {
    if (selectedDocForReject && rejectionReason.trim()) {
      verifyDocument(
        {
          customerId,
          documentId: selectedDocForReject._id,
          verificationStatus: 'rejected',
          rejectionReason: rejectionReason.trim(),
        },
        {
          onSuccess: () => {
            setRejectModalOpen(false);
            setSelectedDocForReject(null);
            setRejectionReason('');
          },
        }
      );
    }
  };

  return (
    <>
      {/* KYC Status */}
      <div className='bg-card border border-border rounded-lg p-6'>
        <h2 className='text-lg font-semibold mb-4 flex items-center'>
          <Shield className='mr-2' size={20} />
          KYC Status
        </h2>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          <div>
            <label className='text-xs text-muted-foreground'>Status</label>
            <p className='text-sm font-medium mt-1 capitalize'>{customer?.kyc?.status?.replace('_', ' ')}</p>
          </div>
          <div>
            <label className='text-xs text-muted-foreground'>Submitted At</label>
            <p className='text-sm font-medium mt-1'>
              {customer?.kyc?.submittedAt ? formatDateTime(customer.kyc.submittedAt) : 'N/A'}
            </p>
          </div>
          <div>
            <label className='text-xs text-muted-foreground'>Approved At</label>
            <p className='text-sm font-medium mt-1'>
              {customer?.kyc?.approvedAt ? formatDateTime(customer.kyc.approvedAt) : 'N/A'}
            </p>
          </div>
          <div>
            <label className='text-xs text-muted-foreground'>Expiry Date</label>
            <p className='text-sm font-medium mt-1'>
              {customer?.kyc?.expiryDate ? formatDateTime(customer.kyc.expiryDate) : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* KYC Documents */}
      <div className='bg-card border border-border rounded-lg p-6'>
        <h2 className='text-lg font-semibold mb-2 flex items-center'>
          <FileText className='mr-2' size={20} />
          KYC Documents
        </h2>
        {customer?.kyc?.documents?.some(
          (doc) => doc.verificationStatus !== 'verified' && doc.verificationStatus !== 'rejected'
        ) && (
          <p className='text-xs text-muted-foreground mb-4'>
            To approve or reject a document, click "View" to review it first. The buttons will be enabled after 5
            seconds.
          </p>
        )}
        <div className='space-y-4'>
          {customer?.kyc?.documents?.length > 0 ? (
            customer.kyc.documents.map((doc) => (
              <div key={doc._id} className='border border-border rounded-lg p-4 hover:bg-accent/50 transition-colors'>
                <div className='flex items-start justify-between'>
                  <div className='flex-1'>
                    <div className='flex items-center gap-2'>
                      <h3 className='font-medium text-sm capitalize'>{doc.type?.replace('_', ' ')}</h3>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(
                          doc.verificationStatus
                        )}`}
                      >
                        {doc.verificationStatus}
                      </span>
                    </div>
                    <p className='text-xs text-muted-foreground mt-1'>Uploaded: {formatDateTime(doc.uploadedAt)}</p>
                    {doc.verifiedAt && (
                      <p className='text-xs text-muted-foreground'>Verified: {formatDateTime(doc.verifiedAt)}</p>
                    )}
                    {doc.rejectionReason && (
                      <p className='text-xs text-red-600 mt-1'>Reason: {doc.rejectionReason}</p>
                    )}
                  </div>
                  <div className='flex gap-2'>
                    <button
                      onClick={() => handleViewDocument(doc._id, doc.documentUrl)}
                      className='px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors'
                    >
                      {viewedDocuments[doc._id] ? 'View Again' : 'View'}
                    </button>
                    {doc.verificationStatus !== 'verified' && doc.verificationStatus !== 'rejected' && (
                      <>
                        <button
                          onClick={() => handleApproveDocument(doc._id)}
                          disabled={!canVerifyDocument(doc._id) || isVerifyingDoc}
                          className={`px-3 py-1 text-xs rounded-md transition-colors ${
                            canVerifyDocument(doc._id)
                              ? 'bg-green-600 text-white hover:bg-green-700 cursor-pointer'
                              : 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
                          }`}
                        >
                          {isVerifyingDoc
                            ? 'Processing...'
                            : !viewedDocuments[doc._id]
                            ? 'View first'
                            : !verifiableDocuments[doc._id]
                            ? 'Wait...'
                            : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleOpenRejectModal(doc)}
                          disabled={!canVerifyDocument(doc._id) || isVerifyingDoc}
                          className={`px-3 py-1 text-xs rounded-md transition-colors ${
                            canVerifyDocument(doc._id)
                              ? 'bg-red-600 text-white hover:bg-red-700 cursor-pointer'
                              : 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
                          }`}
                        >
                          {isVerifyingDoc
                            ? 'Processing...'
                            : !viewedDocuments[doc._id]
                            ? 'View first'
                            : !verifiableDocuments[doc._id]
                            ? 'Wait...'
                            : 'Reject'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className='text-sm text-muted-foreground'>No documents uploaded</p>
          )}
        </div>
      </div>

      {/* Background Check */}
      <div className='bg-card border border-border rounded-lg p-6'>
        <h2 className='text-lg font-semibold mb-4 flex items-center'>
          <Shield className='mr-2' size={20} />
          Background Check
        </h2>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <label className='text-xs text-muted-foreground'>Status</label>
            <p className='text-sm font-medium mt-1 capitalize'>
              {customer?.kyc?.backgroundCheck?.status?.replace('_', ' ') || 'Pending'}
            </p>
          </div>
          <div>
            <label className='text-xs text-muted-foreground'>Checked At</label>
            <p className='text-sm font-medium mt-1'>
              {customer?.kyc?.backgroundCheck?.checkedAt
                ? formatDateTime(customer.kyc.backgroundCheck.checkedAt)
                : 'N/A'}
            </p>
          </div>
          {customer?.kyc?.backgroundCheck?.reportUrl && (
            <div className='md:col-span-2'>
              <a
                href={customer.kyc.backgroundCheck.reportUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors inline-block'
              >
                View Report
              </a>
            </div>
          )}
        </div>
      </div>

      {/* KYC Logs */}
      {customer?.kyc?.logs?.length > 0 && (
        <div className='bg-card border border-border rounded-lg p-6'>
          <h2 className='text-lg font-semibold mb-4'>KYC Activity Logs</h2>
          <div className='space-y-3'>
            {customer.kyc.logs.map((log, index) => (
              <div key={index} className='border-l-2 border-primary pl-4'>
                <p className='text-sm font-medium capitalize'>{log.status?.replace('_', ' ')}</p>
                <p className='text-xs text-muted-foreground'>{formatDateTime(log.changedAt)}</p>
                {log.note && <p className='text-xs mt-1'>{log.note}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reject Document Modal */}
      {rejectModalOpen && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'>
          <div className='bg-card border border-border rounded-lg shadow-lg w-full max-w-md mx-4'>
            <div className='flex items-center justify-between p-6 border-b border-border'>
              <h3 className='text-lg font-semibold text-foreground'>Reject Document</h3>
              <button
                onClick={() => {
                  setRejectModalOpen(false);
                  setSelectedDocForReject(null);
                  setRejectionReason('');
                }}
                className='text-muted-foreground hover:text-foreground transition-colors'
                disabled={isVerifyingDoc}
              >
                <X size={20} />
              </button>
            </div>

            <div className='p-6 space-y-4'>
              <div>
                <label htmlFor='rejectionReason' className='block text-sm font-medium text-foreground mb-2'>
                  Rejection Reason (Required)
                </label>
                <textarea
                  id='rejectionReason'
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary'
                  placeholder='Enter reason for rejection...'
                  required
                />
              </div>

              <div className='flex justify-end gap-3 pt-4'>
                <button
                  type='button'
                  onClick={() => {
                    setRejectModalOpen(false);
                    setSelectedDocForReject(null);
                    setRejectionReason('');
                  }}
                  className='px-4 py-2 text-sm border border-border rounded-md bg-card hover:bg-accent transition-colors'
                  disabled={isVerifyingDoc}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectDocument}
                  disabled={isVerifyingDoc || !rejectionReason.trim()}
                  className='px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {isVerifyingDoc ? 'Rejecting...' : 'Reject Document'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KycTab;
